import React from 'react'
import { v4 as uuid } from 'uuid'
import { useQueries, useQuery } from '@tanstack/react-query'
import { Modal } from '../../../common/Modal'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { theme } from '../../../../theme/theme'
import {
  AccordionSection,
  AccordionSectionItem,
} from '../../../common/AccordionSection'
import { RequestAccordionSectionContent } from './RequestAccordionSectionContent'
import { getUnitRole } from '../../../../helpers/translations/unit-roles'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { IRequestModel } from '../../../../interfaces/models/requests.interfaces'
import { userQueries } from '../../../../api/users'
import { unitQueries } from '../../../../api/units'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { entranceQueries } from '../../../../api/society-entrances'
import { getUserFullName } from '../../../../helpers/user'

interface RequestsModalProps {
  show: boolean
  close: () => void
  requests: IRequestModel[]
  children?: React.ReactNode
}

export const RequestsModal = ({
  children,
  show,
  close,
  requests,
}: RequestsModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()

  const { society } = useCurrentSociety()
  const { data: units } = useQuery(unitQueries.getUnitsPerSociety(society?._id))
  const { data: entrances } = useQuery(
    entranceQueries.getEntrancesPerSociety(society?._id)
  )
  const unitsUserQueries = useQueries({
    queries:
      requests.map((request) => userQueries.getUser(request.userId)) || [],
  })
  const users = unitsUserQueries.flatMap((query) => query.data || [])

  const getRequestSectionData = (
    _request: IRequestModel
  ): AccordionSectionItem => {
    const { unitId, userId, role } = _request
    const user = users.find((_user) => _user._id === userId)
    const unit = unitId
      ? units?.find((_unit) => _unit._id === unitId)
      : undefined

    const getRoleUnitEntranceText = (): string => {
      const strings = []
      if (role) {
        strings.push(translate(getUnitRole(role) as string))
      }
      if (unit && unit.entranceId) {
        const entrance = entrances?.find(
          (_entrance) => _entrance._id === unit.entranceId
        )
        if (entrance) strings.push(entrance.addressStreet)
      }
      if (unit) {
        const unitTitle = unit.title ? `(${unit.title})` : ''
        strings.push(`${unit.titleLegal} ${unitTitle}`)
      }

      return strings.join(', ')
    }

    return {
      id: uuid(),
      title: getUserFullName(user) || '',
      subtitle: getRoleUnitEntranceText(),
      content: <RequestAccordionSectionContent request={_request} />,
    }
  }

  const accordionSectionItems = [] as AccordionSectionItem[]

  requests.forEach((_request) => {
    const user = users.find((_user) => _user._id === _request.userId)

    // User can be null if the requesting user has removed their account
    if (user) {
      accordionSectionItems.push(getRequestSectionData(_request))
    }
  })

  return (
    <>
      {children}
      <Modal show={show}>
        {{
          header: (
            <ModalHeader onClose={close}>
              <p style={theme.textVariants.h3}>
                {translate('registerRequest.modal.title')}
              </p>
            </ModalHeader>
          ),
          body: (
            <ModalBody>
              <AccordionSection
                className="mb-2 md:w-full"
                items={accordionSectionItems}
              />
            </ModalBody>
          ),
        }}
      </Modal>
    </>
  )
}
